import { Box, Button, Chip, Container, Grid, Paper, Stack, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { motion } from 'framer-motion';
import SiteLayout from '../components/SiteLayout';

const MotionPaper = motion(Paper);

const metrics = [
  { label: 'ASN', value: 'AS212348', detail: 'Autonomous System' },
  { label: 'IPv4', value: '31.58.68.0/24', detail: 'Routage principal' },
  { label: 'Upstream', value: 'OVH', detail: 'AnyCast' },
  { label: 'Capacite', value: '17 Tbps', detail: 'Protection DDoS' },
];

const pillars = [
  {
    icon: <ShieldOutlinedIcon sx={{ fontSize: 28 }} />,
    title: 'Mitigation intelligente',
    description: 'Filtrage multicouche L3/L4/L7 avec adaptation en temps reel sur les pics de trafic.',
  },
  {
    icon: <AutoGraphIcon sx={{ fontSize: 28 }} />,
    title: 'Analyse continue',
    description: 'Telemetrie live et observabilite unifiee pour une reaction en quelques secondes.',
  },
  {
    icon: <HubOutlinedIcon sx={{ fontSize: 28 }} />,
    title: 'Backbone robuste',
    description: 'Architecture orientee disponibilite avec priorite a la latence stable et au reroutage.',
  },
];

export default function Home() {
  return (
    <SiteLayout>
      <Container id="top" maxWidth="xl" sx={{ py: { xs: 5, md: 9 } }}>
        <Grid container spacing={4} alignItems="center">
          <Grid item xs={12} md={7}>
            <Stack spacing={3}>
              <Chip
                label="Infrastructure securisee"
                sx={{
                  width: 'fit-content',
                  border: '1px solid rgba(192,132,252,0.35)',
                  backgroundColor: 'rgba(192,132,252,0.12)',
                  color: '#efd9ff',
                }}
              />
              <Typography
                component={motion.h1}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.65 }}
                sx={{
                  fontFamily: '"Sora", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '2.4rem', sm: '3.1rem', md: '4.6rem' },
                  lineHeight: 1,
                  maxWidth: 900,
                }}
              >
                AS212348
                <Box component="span" sx={{ color: 'secondary.main' }}>
                  {' '}
                  Network Defense
                </Box>
              </Typography>
              <Typography
                sx={{
                  maxWidth: 760,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  color: 'text.secondary',
                }}
              >
                Plateforme reseau moderne orientee fiabilite, anti-DDoS et performance. Une stack claire,
                des engagements lisibles et une interface de pilotage plus premium.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button variant="contained" href="#upstream">
                  Voir la protection
                </Button>
                <Button
                  variant="outlined"
                  href="#why"
                  sx={{ borderColor: 'rgba(192,132,252,0.35)', color: '#f0ddff' }}
                >
                  Pourquoi nous
                </Button>
              </Stack>
            </Stack>
          </Grid>

          <Grid item xs={12} md={5}>
            <MotionPaper
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              sx={{
                p: 3,
                background: 'linear-gradient(150deg, rgba(46,22,74,0.92), rgba(21,10,38,0.95))',
                border: '1px solid rgba(192,132,252,0.26)',
              }}
            >
              <Typography variant="h5" sx={{ mb: 2, fontFamily: '"Sora", sans-serif' }}>
                Snapshot reseau
              </Typography>
              <Stack spacing={1.2}>
                {metrics.map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      p: 1.4,
                      borderRadius: 2,
                      border: '1px solid rgba(192,132,252,0.24)',
                      backgroundColor: 'rgba(192,132,252,0.08)',
                    }}
                  >
                    <Typography sx={{ fontSize: 12, letterSpacing: 0.8, textTransform: 'uppercase', color: '#d8b6f3' }}>
                      {item.label}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 23 }}>{item.value}</Typography>
                    <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{item.detail}</Typography>
                  </Box>
                ))}
              </Stack>
            </MotionPaper>
          </Grid>
        </Grid>

        <Box id="upstream" sx={{ mt: { xs: 6, md: 10 } }}>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Informations utiles
          </Typography>
          <Paper
            sx={{
              p: { xs: 2.5, md: 3.5 },
              border: '1px solid rgba(192,132,252,0.35)',
              background:
                'linear-gradient(140deg, rgba(192,132,252,0.22) 0%, rgba(155,77,202,0.12) 70%, rgba(255,255,255,0.02) 100%)',
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography sx={{ letterSpacing: 1.3, color: '#f0d8ff', textTransform: 'uppercase', fontSize: 12 }}>
                  Fournisseur AnyCast
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.5, fontFamily: '"Sora", sans-serif' }}>
                  OVH
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ letterSpacing: 1.3, color: '#f0d8ff', textTransform: 'uppercase', fontSize: 12 }}>
                  Capacite de protection
                </Typography>
                <Typography variant="h3" sx={{ mt: 0.5, color: 'secondary.light', fontFamily: '"Sora", sans-serif' }}>
                  17 Tbps
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Une architecture simplifiee, lisible et ultra-performante pour absorber les attaques de grande
                  ampleur tout en conservant une latence maitrisee.
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        <Box id="why" sx={{ mt: { xs: 6, md: 10 } }}>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Pourquoi nous
          </Typography>
          <Grid container spacing={2.5}>
            {pillars.map((item, index) => (
              <Grid item xs={12} md={4} key={item.title}>
                <MotionPaper
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.35 }}
                  transition={{ duration: 0.45, delay: index * 0.08 }}
                  sx={{ p: 3, height: '100%' }}
                >
                  <Box sx={{ color: 'primary.light', mb: 1.2 }}>{item.icon}</Box>
                  <Typography variant="h6" sx={{ mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ color: 'text.secondary' }}>{item.description}</Typography>
                </MotionPaper>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </SiteLayout>
  );
}
